import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type OwnedShelter = {
  id: string;
  name: string;
  city: string;
  slug: string;
  organization_verified: boolean;
  data_verified: boolean;
  last_updated: string | null;
  current_needs: string | null;
  urgent_needs: boolean | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  volunteer_info: string | null;
  address: string | null;
  description: string | null;
  has_owner: boolean;
  my_role: "owner" | "member";
};

export const useOwnedShelters = (userId: string | undefined) => {
  return useQuery({
    queryKey: ["owned-shelters", userId],
    enabled: !!userId,
    queryFn: async (): Promise<OwnedShelter[]> => {
      const { data: ownerships, error: ownErr } = await supabase
        .from("shelter_owners")
        .select("shelter_id, role")
        .eq("user_id", userId!);
      if (ownErr) throw ownErr;
      const rows = ownerships ?? [];
      const ids = rows.map((o) => o.shelter_id);
      if (ids.length === 0) return [];
      const roleByShelter = new Map(rows.map((o) => [o.shelter_id, (o.role as "owner" | "member") ?? "member"]));

      const { data, error } = await supabase
        .from("shelters")
        .select("id, name, city, slug, organization_verified, data_verified, last_updated, current_needs, urgent_needs, phone, email, website, volunteer_info, address, description, has_owner")
        .in("id", ids)
        .order("name");
      if (error) throw error;
      return (data ?? []).map((s: any) => ({
        ...s,
        my_role: roleByShelter.get(s.id) ?? "member",
      })) as OwnedShelter[];
    },
  });
};
